<template>
  <div id="login-view">
    <!-- 처음 로딩시 레이아웃이 깨진 화면이 깜빡하는 현상, 하얀 로딩창 먼저 보여줌  -->
    <div v-if="full_loading" class="full_loading"></div>

    <!-- 넓은 화면은 좌우 나눔(POS2 Lite 로그인과 같은 틀) — 왼쪽 띠에 로고와 한 줄, 오른쪽에 로그인 카드. 좁은 화면은 위아래로 쌓는다 -->
    <aside class="login-brand">
      <div class="login-title">
        <img src="/img/logo-img.png?v=1" />
      </div>
      <p class="login-brand-line">{{ $t(adminCopy.brandLine) }}</p>
      <!-- 띠 맨 아래 — 개발사 표시(POS2 Lite 로그인 화면 왼쪽 칸 맨 아래와 같은 두 줄) -->
      <footer class="login-brand-footer" data-testid="developer-attribution">
        <span>{{ $t(attribution.contact) }}</span>
        <span>{{ attribution.poweredBy }}</span>
      </footer>
    </aside>

    <main class="login-main">
      <div class="contain">
        <!-- 처음 받은 비밀번호 그대로인 계정(appserver 결정 47) — 휴대폰 확인 뒤 새 비밀번호. 막기(로그인 전)와 경고의 「지금 바꾸기」(로그인 뒤)가 같은 칸이다 -->
        <password-reset-panel
          v-if="resetToken"
          :reset-token="resetToken"
          :account-id="accountId"
          :back-text="pendingAuth ? resetText.later : resetText.back"
          @done="onResetDone"
          @leave="onResetLeave"></password-reset-panel>

        <!-- 사장님 계정에 매장이 여럿 — 같은 카드 안에서 고른다 -->
        <owner-store-picker
          v-else-if="ownerPick"
          :account-label="ownerPick.accountLabel"
          :stores="ownerPick.stores"
          :error="ownerPickError"
          @enter="pickOwnerStore"
          @other-account="leaveOwnerPick"></owner-store-picker>

        <!-- 사장님 계정 비밀번호 찾기 — 같은 카드 안에서 끝난다 -->
        <owner-password-reset
          v-else-if="ownerResetOpen"
          @done="onOwnerResetDone"
          @back="ownerResetOpen = false"></owner-password-reset>

        <div class="login-panel" v-else>
          <form @submit.prevent="handleSubmit">
            <!-- 언어 선택 -->
            <el-select v-model="locale" @change="lanChange" class="locale-select">
              <el-option v-for="item in lan" :key="item.id" :label="item.nm" :value="item.id"></el-option>
            </el-select>

            <!-- 머리 → 탭 → 칸 — POS2 Lite 로그인과 같은 차례. 머리는 탭을 따라 바뀐다 -->
            <owner-head :title="$t(loginHead.title)" :sub="loginHead.sub && $t(loginHead.sub)"></owner-head>

            <!-- 휴대폰 번호·이메일은 사장님 계정(POS2 Lite·오더엔 멀티와 같은 계정), 아이디는 지금의 계정 로그인 -->
            <div class="login-tabs" role="tablist">
              <button
                v-for="tab in tabs"
                :key="tab"
                type="button"
                role="tab"
                class="login-tab"
                :class="{ 'is-active': loginTab === tab }"
                :aria-selected="loginTab === tab ? 'true' : 'false'"
                :data-tab="tab"
                @click="selectTab(tab)">
                {{ $t(tabLabel[tab]) }}
              </button>
            </div>

            <template v-if="ownerTab">
              <label class="login-label" for="owner-login-id">{{ $t(ownerIdLabel) }}</label>
              <el-input
                id="owner-login-id"
                v-keep-number-caret="loginTab === tabIds.PHONE"
                :value="ownerLoginId"
                :placeholder="$t(ownerIdPlaceholder)"
                :inputmode="loginTab === tabIds.PHONE ? 'numeric' : 'email'"
                name="ownerLoginId"
                autocomplete="username"
                clearable
                autofocus="true"
                @input="onOwnerLoginIdInput"></el-input>

              <label class="login-label" for="owner-login-password">{{ $t(ownerCopy.password) }}</label>
              <el-input
                id="owner-login-password"
                v-model="ownerPassword"
                :placeholder="$t(ownerCopy.password)"
                name="ownerPassword"
                autocomplete="current-password"
                clearable
                type="password"></el-input>
            </template>

            <template v-else>
              <el-input :placeholder="$t('아이디')" name="accountId" v-model="accountId" clearable autofocus="true">
                <i class="el-input__icon icon iconfont icon-people" slot="prefix"></i>
              </el-input>

              <el-input :placeholder="$t('비밀번호')" name="accountPw" v-model="accountPw" clearable type="password">
                <i class="el-input__icon icon iconfont icon-lock" slot="prefix"></i>
              </el-input>
            </template>

            <!-- 오류 메세지 표시 구역 -->
            <div class="error-messages">
              <!-- 로그인 실패는 **여기 한 곳**이 말한다(요청의 토스트는 끈다) — 문장은 `utils/loginFailure`·`utils/ownerCopy` 가 고른다 -->
              <div v-if="loginError" id="msg" class="msg">{{ loginError }}</div>
              <div v-if="captchaError" id="captcha-msg" class="msg">{{ $t('보안 인증을 통과하지 못했습니다.') }}</div>
            </div>

            <!-- Turnstile 캡차 추가 (token 로그인이 아닐 때만 표시) — 세 탭이 위젯 하나를 같이 쓴다 -->
            <turnstile-captcha
              v-if="requiresCaptcha"
              ref="captcha"
              :site-key="turnstileSiteKey"
              @captcha-success="onCaptchaSuccess"
              @captcha-expired="onCaptchaExpired"
              @captcha-error="onCaptchaError">
            </turnstile-captcha>

            <el-button type="primary" native-type="submit" class="btn_login">{{ $t('로그인') }}</el-button>

            <!-- 사장님 계정 — 비밀번호는 휴대폰 본인확인으로 찾는다. 가입은 아직 앱에만 있어 링크가 없다 -->
            <div v-if="ownerTab" class="owner-login-links">
              <el-button type="text" class="owner-forgot" @click="openOwnerReset">{{
                $t(ownerCopy.forgotPassword)
              }}</el-button>
              <!-- 예전 S… 아이디 입구(POS2 Lite 첫 화면과 같은 줄) — 관리 웹은 [아이디] 탭이 그 입구다 -->
              <el-button type="text" class="owner-legacy" @click="selectTab(tabIds.ID)">{{
                $t(ownerCopy.legacyEntry)
              }}</el-button>
              <p class="owner-join">{{ $t(adminCopy.joinLine) }}</p>
            </div>

            <!-- 비밀번호를 잊었을 때 — 고객센터가 계정 관리에서 새 비밀번호를 정해 준다(메일 링크로 바꾸던 흐름은 S5 에서 지웠다) -->
            <div v-else class="forget-support">
              <p class="forget-guide">{{ $t('비밀번호를 잊으셨으면 카카오톡 고객센터로 문의해 주세요.') }}</p>
              <el-button class="btn_support" @click="openSupport">{{ $t(resetText.support) }}</el-button>
            </div>
          </form>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import TurnstileCaptcha from '@/components/TurnstileCaptcha.vue'
import PasswordResetPanel from '@/components/login/PasswordResetPanel.vue'
import OwnerHead from '@/components/login/OwnerHead.vue'
import OwnerPasswordReset from '@/components/login/OwnerPasswordReset.vue'
import OwnerStorePicker from '@/components/login/OwnerStorePicker.vue'
import { HttpError, logHttpFailure } from '@/plugins/http'
import { LOOKUP_REQUEST_CONFIG } from '@/plugins/http/policy'
import { loginFailureText } from '@/utils/loginFailure'
import {
  DEFAULT_PASSWORD_CHOICE,
  PASSWORD_RESET_TEXT,
  askDefaultPasswordChange,
  defaultPasswordNoticeOf,
  passwordResetTokenOf,
} from '@/utils/passwordReset'
import { safeRedirectPage } from '@/utils/loginRedirect'
import { telTyping } from '@/utils/telNumber'
import { KAKAO_SUPPORT_CHAT_URL } from '@/utils/constants'
import { hasAction } from '@/utils/authActions'
import {
  MINE_APPLICATION_API,
  PENDING_APPLICATION_READ_CONFIG,
  STORE_APPLICATION_ACTION,
  STORE_APPLICATION_PATH,
  STORE_APPLICATION_TEXT,
  applicationTitleOf,
  applicationsOf,
  pendingApplicationOf,
  pendingNoticeOf,
} from '@/utils/storeApplication'
import {
  ADMIN_LOGIN_COPY,
  DEVELOPER_ATTRIBUTION,
  LOGIN_TAB_LABEL,
  OWNER_COPY,
  UNKNOWN_TEXT,
  ownerAccountLabel,
  ownerEnterStoreRejectionText,
  ownerFailureText,
  ownerLoginInputText,
  ownerLoginRejectionText,
} from '@/utils/ownerCopy'
import {
  DEFAULT_LOGIN_TAB,
  LOGIN_TAB,
  LOGIN_TABS,
  LOGIN_TAB_STORAGE_KEY,
  OWNER_REQUEST_CONFIG,
  OWNER_URL,
  isOwnerTab,
  isPhoneTyping,
  loginTabOf,
  ownerEnterStoreBody,
  ownerLoginBody,
  ownerLoginIdOf,
  ownerLoginInputProblem,
  ownerLoginViewOf,
  soleStoreCdOf,
} from '@/utils/ownerLogin'

/**
 * 로그인 요청의 config — **토스트를 끈다**(§16.175 옵트아웃). 실패는 폼의 오류 칸이 말한다([loginFailureText]).
 * 토스트가 함께 뜨면 서버 문장과 폼 문구가 두 말을 한다(비활성 조직 거부에서는 둘이 서로 다른 사실을 말했다).
 */
const LOGIN_REQUEST_CONFIG = Object.freeze({ showErrorMessage: false })

/**
 * 기억한 탭 — 브라우저 저장소를 못 읽으면(사생활 창·막힌 저장소에서는 `localStorage` 에 닿기만 해도 던진다) 처음 탭이다. 값 판정은
 * `utils/ownerLogin.loginTabOf` 한 곳이고, 여기는 저장소에 닿는 효과만 한다.
 */
function storedLoginTab() {
  try {
    return loginTabOf(window.localStorage.getItem(LOGIN_TAB_STORAGE_KEY))
  } catch (error) {
    return DEFAULT_LOGIN_TAB
  }
}

/** 로그인한 탭을 기억한다 — 편의다. 못 쓰면 다음에도 처음 탭으로 열 뿐 화면은 그대로 돈다 */
function rememberLoginTab(tab) {
  try {
    window.localStorage.setItem(LOGIN_TAB_STORAGE_KEY, tab)
  } catch (error) {
    // 저장소가 막혔다 — 기억하지 못해도 로그인은 그대로 간다
  }
}

export default {
  components: {
    TurnstileCaptcha,
    PasswordResetPanel,
    OwnerHead,
    OwnerPasswordReset,
    OwnerStorePicker,
  },
  data() {
    return {
      full_loading: true,
      loading_: null,
      lan: [],
      // 선택칸의 값 — i18n 의 지금 로케일(기본·폴백 `ko` 는 i18n 이 소유한다)
      locale: this.$i18n.locale,
      /** 로그인 탭 — 처음은 휴대폰 번호, 마지막에 로그인한 탭을 기억한다 */
      loginTab: storedLoginTab(),
      tabs: LOGIN_TABS,
      tabIds: LOGIN_TAB,
      tabLabel: LOGIN_TAB_LABEL,
      ownerCopy: OWNER_COPY,
      adminCopy: ADMIN_LOGIN_COPY,
      attribution: DEVELOPER_ATTRIBUTION,
      /** 휴대폰·이메일 탭의 칸 — 휴대폰은 하이픈을 넣어 보이고 서버엔 숫자만 보낸다(`ownerLoginBody`) */
      ownerLoginId: '',
      ownerPassword: '',
      /** 사장님 계정에 매장이 여럿 — `{loginTicket, accountLabel, stores}` 가 있으면 로그인 칸 대신 매장 고르기가 선다 */
      ownerPick: null,
      /** 매장 고르기에서 들어가지 못한 한 줄 */
      ownerPickError: '',
      /** 사장님 계정 비밀번호 찾기 — 켜면 로그인 칸 대신 그 칸이 선다 */
      ownerResetOpen: false,
      accountId: '',
      accountPw: '',
      turnstileSiteKey: process.env.VUE_APP_CLOUDFLARE_TURNSTILE_SITE_KEY,
      captchaToken: null,
      captchaError: false,

      appMsg: {
        loadingMsg: this.$t('처리중 입니다...'),
        systemAlterTitle: this.$t('시스템 메세지'),
        systemAlterButton: this.$t('로그인 페이지로 돌아가기'),
        systemAlterTimeoutMsg: this.$t('연결 시간 초과 하였습니다. 다시 로그인 하십시오.'),
        systemAlterNoPermissionsMsg: this.$t('권한 부족하여 리소스에 접근 할 수 없습니다.'),
        systemBtnOk: this.$t('확인'),
        systemBtnClose: this.$t('닫기'),
      },

      /** 폼 오류 칸의 문장 — 빈 문자열이면 칸이 서지 않는다 */
      loginError: '',
      /** 처음 받은 비밀번호 재설정 토큰 — 있으면 로그인 칸 대신 재설정 칸이 선다 */
      resetToken: null,
      /**
       * 경고(막기 시작 전)에서 「지금 바꾸기」를 고른 로그인 응답 — 서버 세션은 이미 열렸다. 재설정 칸이 끝나면(바꿨든 나갔든) 이 값으로
       * 들어간다. 그 사이에는 화면의 로그인 상태를 만들지 않는다(새로고침하면 로그인 칸 — 다시 로그인하면 다시 묻는다)
       */
      pendingAuth: null,
      resetText: PASSWORD_RESET_TEXT,
      isTokenLogin: false, // token 로그인 여부 추적
    }
  },
  created() {
    // 언어 콤보 초기화
    this.initLanguage()
    this.checkMessageParam()
    this.checkTokenParam()
    this.checkAccountParam()
  },
  mounted() {
    // 로딩 이미지 숨김
    this.full_loading = false
  },
  watch: {
    accountId() {
      var reg = new RegExp(/[s|c]\d{9}/)

      if (reg.test(this.accountId)) {
        this.accountId = this.accountId.toUpperCase()
      }
    },
  },
  computed: {
    requiresCaptcha() {
      const localCompose = process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOCAL_COMPOSE === 'true'
      return !this.isTokenLogin && !localCompose
    },
    /** 휴대폰 번호·이메일 탭 — 사장님 계정으로 로그인한다 */
    ownerTab() {
      return isOwnerTab(this.loginTab)
    },
    /** 로그인 칸의 머리 — 사장님 탭은 정본(Lite) 머리, 아이디 탭은 관리 웹의 머리와 한 줄 */
    loginHead() {
      return this.ownerTab
        ? { title: OWNER_COPY.loginHeading, sub: '' }
        : { title: ADMIN_LOGIN_COPY.idHeading, sub: ADMIN_LOGIN_COPY.idSub }
    },
    ownerIdLabel() {
      return this.loginTab === LOGIN_TAB.PHONE ? OWNER_COPY.tabPhone : OWNER_COPY.tabEmail
    },
    ownerIdPlaceholder() {
      return this.loginTab === LOGIN_TAB.PHONE ? OWNER_COPY.phonePlaceholder : OWNER_COPY.emailPlaceholder
    },
  },
  methods: {
    checkMessageParam() {
      const params = new URLSearchParams(window.location.search)
      const msg = params.get('redirect_reason')

      console.log(msg)

      if (!msg) return

      switch (msg) {
        case '403':
          this.$message.error(this.appMsg.systemAlterNoPermissionsMsg)
          break
      }
    },
    checkTokenParam() {
      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      const redirectPage = params.get('page')

      if (token) {
        this.isTokenLogin = true
        this.loginWithToken(token, redirectPage)
      }
    },
    /**
     * 콜팝 웹 가입이 이미 등록된 사업자로 막혀 「이 아이디로 로그인」을 고르고 왔다(`?account=S…`) — 예전 아이디의 입구인 [아이디] 탭에 그 아이디를
     * 채워 연다(POS2 Lite 가 예전 아이디 화면을 그 아이디로 여는 것과 같다)
     */
    checkAccountParam() {
      const account = this.$route.query.account
      if (typeof account !== 'string' || !account.trim()) return

      this.loginTab = LOGIN_TAB.ID
      this.accountId = account.trim()
    },
    async handleSubmit() {
      // token 로그인이 아닐 때만 캡차 토큰 검증
      if (this.requiresCaptcha) {
        const captchaToken = this.currentCaptchaToken()

        if (!captchaToken) {
          this.captchaError = true
          return
        }
      }

      this.captchaError = false
      rememberLoginTab(this.loginTab)

      // 휴대폰 번호·이메일 탭은 사장님 계정 입구로 — 들어간 뒤는 아래와 같은 성공 처리(`afterLogin`) 한 곳이다
      if (this.ownerTab) {
        await this.submitOwnerLogin()
        return
      }

      this.showLoading()

      try {
        const requestData = {
          accountId: this.accountId,
          accountPw: this.accountPw,
        }

        // token 로그인이 아닐 때만 캡차 토큰 추가
        if (!this.isTokenLogin) {
          requestData.captcha = this.currentCaptchaToken()
        }

        const result = await this.$axios.post('/admin/api/login', requestData, LOGIN_REQUEST_CONFIG)

        // 묻는 창을 잠금 로딩 아래에 두지 않는다
        this.closeLoading()
        await this.afterLogin(result.data)
      } catch (error) {
        // 처음 받은 비밀번호 그대로 — 실패가 아니라 다음 할 일이다(재설정 칸이 로그인 칸을 대신한다)
        const resetToken = passwordResetTokenOf(error)
        if (resetToken) {
          this.accountPw = ''
          this.loginError = ''
          this.resetToken = resetToken
        } else {
          this.loginError = loginFailureText(error, this.$t('아이디, 비밀번호를 정확히 입력 하십시오.'))
          // 서버는 보안 인증 토큰을 한 번만 받는다 — 다음 시도는 새 토큰으로(쓴 토큰이면 「보안 인증을 통과하지 못했습니다.」)
          this.renewCaptcha()
        }

        // 오류를 통째로 찍지 않는다 — 전송 실패면 요청 본문(아이디·비밀번호·캡차 토큰)이 실려 있다(리뷰 N1)
        logHttpFailure(error, 'login failed: ')
      }

      this.closeLoading()
    },
    async loginWithToken(token, redirectPage) {
      this.showLoading()

      try {
        // token 로그인은 캡차 검증 없이 token만으로 인증
        const result = await this.$axios.post(
          '/admin/api/login',
          {
            token: token,
          },
          LOGIN_REQUEST_CONFIG
        )

        this.$store.dispatch('auth/setAuth', result.data)

        // `page` 는 **등록된 라우트일 때만** 목적지다 (브리프 D3) — POS 가 실어 보내는 값을 검증 없이 따라가면
        // 열린 리다이렉트다. 매치 여부는 라우터가 답하고(`resolve`), 쿼리스트링(`?storeCd=`)은 보존된다.
        const targetPath = safeRedirectPage(redirectPage, (path) => this.$router.resolve(path).route.matched.length > 0)
        // ★ **목적지를 최초 내비게이션으로 만든다** (2026-09-03 리뷰 지적).
        //
        // 앱 안에서 `$router.replace` 로 넘어가면 그 이동은 **최초 내비게이션이 아니다** — `onReady` 는 이미
        // `/login` 에서 성공했으므로, 목적지가 지연 청크이고 배포 직후 그 청크를 못 받으면 오류 복구
        // (`main.js` 의 `onReady` 오류 콜백 · 1회 새로고침)가 돌지 않는다. 이동은 조용히 거절되고 화면은
        // 로그인 폼에 남는데 **토큰은 이미 소비돼** 다시 시도할 수도 없다 — POS 창에서는 그것이 막다른 길이다.
        //
        // 문서를 새로 여는 비용은 한 번이고, 세션은 쿠키와 `sessionStorage` 양쪽에 있어 그대로 살아남는다.
        window.location.replace(targetPath)
        return
      } catch (error) {
        // token 로그인 실패 시 일반 로그인 모드로 전환
        this.isTokenLogin = false

        // 토큰 입구에는 사람이 넣은 아이디·비밀번호가 없다 — 고정 문구 대신 서버의 사유를 폼에 앉힌다
        this.loginError = loginFailureText(error)
        if (error instanceof HttpError) {
          this.$message.error(this.$t('토큰 로그인에 실패했습니다.'))
        }

        logHttpFailure(error, 'Token login error:')
      }

      this.closeLoading()
    },
    /**
     * 사장님 계정 로그인(휴대폰 번호·이메일 탭) — 입력을 먼저 보고(`ownerLoginInputProblem`), `POST /admin/api/login/owner` 로 매장 목록을 받는다.
     * 매장이 한 곳이면 바로 들어가고, 여럿이면 같은 카드 안에 매장 고르기를 세운다. 실패는 폼의 오류 칸 한 곳이 말하고 보안 인증은 새 토큰을 받는다
     * (아이디 로그인과 같은 규칙).
     */
    async submitOwnerLogin() {
      const tab = this.loginTab
      const problem = ownerLoginInputProblem(tab, this.ownerLoginId, this.ownerPassword)
      if (problem) {
        this.loginError = this.$t(ownerLoginInputText(problem))
        return
      }

      this.showLoading()

      try {
        const result = await this.$axios.post(
          OWNER_URL.LOGIN,
          ownerLoginBody(tab, this.ownerLoginId, this.ownerPassword, this.currentCaptchaToken()),
          OWNER_REQUEST_CONFIG
        )
        const view = ownerLoginViewOf(result.data)
        const storeCd = soleStoreCdOf(view)

        if (!view) {
          this.loginError = this.$t(UNKNOWN_TEXT)
          this.renewCaptcha()
        } else if (storeCd === null) {
          // 고를 매장이 여럿(또는 없음) — 계정 표시는 친 아이디(휴대폰은 하이픈)
          this.ownerPassword = ''
          this.loginError = ''
          this.ownerPickError = ''
          this.ownerPick = {
            loginTicket: view.loginTicket,
            accountLabel: ownerAccountLabel(ownerLoginIdOf(tab, this.ownerLoginId)),
            stores: view.stores,
          }
        } else {
          const failure = await this.enterOwnerStore(view.loginTicket, storeCd)
          if (failure) {
            this.loginError = failure
            this.renewCaptcha()
          }
        }
      } catch (error) {
        this.loginError = this.$t(ownerFailureText(error, (rejection) => ownerLoginRejectionText(rejection, tab)))
        this.renewCaptcha()
        // 오류를 통째로 찍지 않는다 — 전송 실패면 요청 본문(아이디·비밀번호·캡차 토큰)이 실려 있다(리뷰 N1)
        logHttpFailure(error, 'owner login failed: ')
      }

      this.closeLoading()
    },
    /**
     * 고른 매장으로 들어간다 — `POST /admin/api/login/owner/store` 의 답은 아이디 로그인과 같은 `LoginResponse` 라 같은 성공 처리(`afterLogin`)
     * 한 곳으로 간다. 들어갔으면 빈 글자, 못 들어갔으면 그 한 줄(번역된 것)을 돌려준다 — 어디에 앉힐지는 부른 쪽이 정한다(실패는 값이다).
     * 잠금 로딩은 부른 쪽이 띄운다.
     */
    async enterOwnerStore(loginTicket, storeCd) {
      try {
        const result = await this.$axios.post(
          OWNER_URL.ENTER_STORE,
          ownerEnterStoreBody(loginTicket, storeCd),
          OWNER_REQUEST_CONFIG
        )

        // 묻는 창(처음 비밀번호 경고)을 잠금 로딩 아래에 두지 않는다 — 아이디 로그인과 같다
        this.closeLoading()
        await this.afterLogin(result.data)
        return ''
      } catch (error) {
        logHttpFailure(error, 'owner store login failed: ')
        return this.$t(ownerFailureText(error, ownerEnterStoreRejectionText))
      }
    },
    /** 매장 고르기에서 한 곳을 눌렀다 — 못 들어가면 고르기 칸이 그 한 줄을 말한다 */
    async pickOwnerStore(storeCd) {
      this.ownerPickError = ''
      this.showLoading()
      const failure = await this.enterOwnerStore(this.ownerPick.loginTicket, storeCd)
      this.closeLoading()
      if (failure) this.ownerPickError = failure
    },
    /** 「다른 계정으로 로그인」 — 로그인 표를 버리고 로그인 칸으로(보안 인증 위젯은 로그인 칸과 함께 새로 선다) */
    leaveOwnerPick() {
      this.ownerPick = null
      this.ownerPickError = ''
      this.ownerPassword = ''
      this.loginError = ''
    },
    /** 탭을 바꾼다 — 칸과 오류 줄을 비운다(POS2 Lite 와 같다). 아이디 탭의 칸은 그대로 둔다 */
    selectTab(tab) {
      if (tab === this.loginTab) return
      this.loginTab = tab
      this.ownerLoginId = ''
      this.loginError = ''
    },
    /** 휴대폰 탭에서 번호 꼴로 치면 번호 종류대로 하이픈을 넣어 보인다(`telNumber.telTyping`) — 서버엔 숫자만 간다. 그 밖(이메일·S… 아이디)은 친 그대로 */
    onOwnerLoginIdInput(value) {
      this.ownerLoginId = this.loginTab === LOGIN_TAB.PHONE && isPhoneTyping(value) ? telTyping(value) : value
    },
    /** 「비밀번호를 잊었어요」 — 같은 카드 안에서 본인확인 → 새 비밀번호 */
    openOwnerReset() {
      this.loginError = ''
      this.ownerResetOpen = true
    },
    /** 사장님 비밀번호를 바꿨다 — 휴대폰 탭에 그 번호를 채워 로그인 칸으로 돌아간다(보안 인증과 함께 새 비밀번호로 로그인한다) */
    onOwnerResetDone(loginId) {
      this.ownerResetOpen = false
      this.loginTab = LOGIN_TAB.PHONE
      this.ownerLoginId = ownerAccountLabel(loginId)
      this.ownerPassword = ''
      this.loginError = ''
      this.$message({ type: 'success', message: this.$t(OWNER_COPY.passwordChanged) })
    },
    /**
     * 로그인이 됐다 — 아이디·비밀번호 로그인과 사장님 계정 매장 고르기가 같이 쓰는 성공 처리 한 곳. 처음 비밀번호 경고(막기 시작 전, appserver
     * 결정 47 10-18 고침)가 실려 오면 로그인할 때마다 「지금 바꾸기 / 나중에」를 묻는다. 지금 바꾸기면 재설정 칸, 아니면 들어간다
     */
    async afterLogin(data) {
      const notice = defaultPasswordNoticeOf(data)
      if (notice) {
        const choice = await askDefaultPasswordChange(this.$confirm.bind(this), notice)
        if (choice === DEFAULT_PASSWORD_CHOICE.NOW) {
          this.pendingAuth = data
          this.accountPw = ''
          this.resetToken = notice.resetToken
          return
        }
      }
      this.enterApp(data)
    },
    /** 로그인 상태를 싣고 들어간다 — 갈 곳은 [landingAfterLogin] */
    async enterApp(data) {
      this.$store.dispatch('auth/setAuth', data)
      this.$router.replace(await this.landingAfterLogin())
    },
    /**
     * 로그인 뒤 갈 곳 — 로그인 화면이 받은 돌아갈 곳(`page` — 토큰 로그인과 같은 인자, 등록된 앱 안 경로만 `safeRedirectPage`)이 있으면 그곳이다
     * (휴대폰 서류 올리기의 [로그인해서 올리기]). 없으면 사장님 세션에 아직 할 일이 남은 신청(서류 미제출·일부·보완 요청 —
     * `storeApplication.pendingApplicationOf`)이 있을 때 그렇게 말하고 묻는다: [서류 올리러 가기]면 서류 화면, 아니면 처음 화면(`/` — 역할별
     * 홈). 신청 조회는 그 액션을 가진 세션만 부르고, 못 읽으면 처음 화면이다(계획 callpop-web-signup §11.9).
     */
    async landingAfterLogin() {
      const requested = safeRedirectPage(
        this.$route.query.page,
        (path) => this.$router.resolve(path).route.matched.length > 0
      )
      if (requested !== '/') return requested

      if (!hasAction(this.$store.getters['auth/auth'], STORE_APPLICATION_ACTION.MINE)) return '/'

      let pending = null
      try {
        const result = await this.$axios.get(MINE_APPLICATION_API, PENDING_APPLICATION_READ_CONFIG)
        pending = pendingApplicationOf(applicationsOf(result.data))
      } catch (error) {
        logHttpFailure(error, 'pending store application read failed: ')
      }
      if (!pending) return '/'

      try {
        await this.$confirm(this.$t(pendingNoticeOf(pending)), this.$t(applicationTitleOf(pending.kindName)), {
          confirmButtonText: this.$t(STORE_APPLICATION_TEXT.pendingConfirm),
          cancelButtonText: this.$t(STORE_APPLICATION_TEXT.pendingLater),
          type: 'info',
        })
        return STORE_APPLICATION_PATH.MINE
      } catch (later) {
        return '/'
      }
    },
    /** 보안 인증 위젯의 지금 토큰 — 위젯이 없는 실행(토큰 로그인·compose)이면 `null` */
    currentCaptchaToken() {
      return this.$refs.captcha ? this.$refs.captcha.getToken() : null
    },
    /** 보안 인증 위젯이 토큰을 새로 받게 한다 — 위젯이 없는 개발 실행(compose)이면 할 일이 없다 */
    renewCaptcha() {
      this.captchaToken = null
      if (this.$refs.captcha) this.$refs.captcha.resetWidget()
    },
    /**
     * 새 비밀번호로 바꿨다 — 경고에서 왔으면(이미 로그인) 그대로 들어가고, 막기에서 왔으면 로그인 칸으로 돌아가 새 비밀번호로 로그인한다
     * (보안 인증 위젯은 로그인 칸과 함께 새로 선다)
     */
    onResetDone(message) {
      const pending = this.pendingAuth
      this.resetToken = null
      this.pendingAuth = null
      this.accountPw = ''
      if (pending) {
        this.$message({ type: 'success', message: this.$t(PASSWORD_RESET_TEXT.doneContinue) })
        this.enterApp(pending)
        return
      }
      this.$message({ type: 'success', message: this.$t(message) })
    },
    /**
     * 바꾸지 않고 나갔다 — 경고에서 왔으면 「나중에」와 같다(들어간다, 확인 시간이 지났으면 다음 로그인 때 다시 묻는다고 말한다). 막기에서
     * 왔으면 로그인 칸으로 — 들고 온 문장(토큰이 끝났다 등)은 로그인 칸의 오류 자리가 말한다
     */
    onResetLeave(message) {
      const pending = this.pendingAuth
      this.resetToken = null
      this.pendingAuth = null
      this.accountPw = ''
      if (pending) {
        if (message) this.$message({ type: 'warning', message: this.$t(PASSWORD_RESET_TEXT.retryNextLogin) })
        this.enterApp(pending)
        return
      }
      this.loginError = message ? this.$t(message) : ''
    },
    // 캡차 처리 메서드
    onCaptchaSuccess(token) {
      this.captchaToken = token
      this.captchaError = false
    },
    onCaptchaExpired() {
      this.captchaToken = null
    },
    onCaptchaError(error) {
      console.error('캡차 오류:', error)
      this.captchaError = true
    },
    /**
     * 언어 정보 설정 — 작고 화면 본체가 아닌 조회라 가벼운 조회 프로필(짧은 타임아웃 + 재시도, 잠금 로딩·토스트 없음)로
     * 부른다.
     *
     * 끝내 못 받으면 선택칸을 **지금 로케일 한 줄**로 채우고 조용히 끝낸다(프로필의 「토스트 없음」에 대한 이 화면의 대체
     * 동작). 종전에는 실패 처리가 없어 선택칸이 빈 채 남고 거부가 처리되지 않았다. 대체값을 새로 적지 않는다 — 출처는
     * 선택칸의 값(i18n 의 지금 로케일)이다.
     */
    async initLanguage() {
      try {
        this.lan = (await this.$axios.get('/language', LOOKUP_REQUEST_CONFIG)).data
      } catch (error) {
        this.lan = [{ id: this.locale, nm: this.locale }]
      }
    },
    // Loading 표시
    showLoading() {
      this.loading_ = this.$loading({
        lock: true,
        text: this.appMsg.loadingMsg,
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)',
      })
    },
    // Loading 닫기 — 로그인 성공은 묻기 전에 먼저 닫으므로 두 번 불릴 수 있다
    closeLoading() {
      if (null != this.loading_) this.loading_.close()
      this.loading_ = null
    },
    // 언어 바꿈 이벤트 설정
    lanChange(val) {
      this.$i18n.locale = val
    },
    // 카카오톡 고객센터 — 처음 비밀번호 재설정 칸의 고객센터 단추와 같은 주소·같은 방식(새 창, noopener)
    openSupport() {
      window.open(KAKAO_SUPPORT_CHAT_URL, '_blank', 'noopener')
    },
  },
}
</script>

<style scoped>
/* 좌우 나눔 — 왼쪽 띠(브랜드 색, POS2 Lite 로그인과 같은 틀)와 오른쪽 카드. 좁은 화면은 아래 미디어 질의가 위아래로 쌓는다 */
#login-view {
  display: flex;
  width: 100%;
  min-height: 100vh;
  background: var(--color-bg-page);
  color: var(--color-text-primary);
}
.full_loading {
  width: 100%;
  height: 100%;
  background-color: white;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 999;
}
.login-brand {
  flex: 0 0 38%;
  min-width: 320px;
  max-width: 520px;
  padding: 48px 40px;
  background: var(--color-primary);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
/* 개발사 표시 — 띠 맨 아래 두 줄, 작은 흐린 흰 글(POS2 Lite `DeveloperAttribution` tone onBrand 와 같은 모양) */
.login-brand-footer {
  margin-top: auto;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(255, 255, 255, 0.75);
}
/* 로고는 주황 원이 들어 있어 주황 띠 위에 바로 놓으면 묻힌다 — 흰 판에 얹는다 */
.login-title {
  display: inline-block;
  padding: 16px 20px;
  background-color: var(--color-bg-card);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.login-title img {
  display: block;
  width: 200px;
}
.login-brand-line {
  margin: 24px 0 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 32px;
  word-break: keep-all;
}
.login-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}
.contain {
  width: 400px;
  max-width: 100%;
  padding: 40px 40px 0 40px;
  background-color: var(--color-bg-card);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
}
.login-panel {
  text-align: center;
}
.el-select {
  width: 100%;
  margin-top: 20px;
}
.el-input {
  margin-bottom: 20px;
}
.iconfont {
  padding-left: 4px;
}
.forget-support {
  /* 카드(.contain)는 아래 안쪽 여백이 0 이라 마지막 칸의 바깥 여백은 카드 밖으로 빠진다 — 테두리 있는 단추가 카드 끝에 붙지 않게 안쪽 여백으로 둔다 */
  margin-top: 20px;
  padding-bottom: 20px;
}
.forget-guide {
  text-align: left;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 12px;
}
.error-messages {
  margin-bottom: 15px;
  min-height: 20px;
}
.msg {
  width: 100%;
  text-align: left;
  font-size: 14px;
  line-height: 16px;
  color: red;
  margin-bottom: 5px;
  display: block;
}
.btn_login,
.btn_support {
  width: 100%;
  height: 42px;
  font-size: 15px;
  font-weight: 600;
  border-radius: var(--radius-sm);
}
.locale-select {
  /* 로고가 왼쪽 띠로 옮겨 가 언어 선택이 카드 맨 위다 — 카드 안쪽 여백 위에 바깥 여백을 더 얹지 않는다 */
  margin-top: 0;
  margin-bottom: 20px;
}
/* 탭 — 세 칸 고르기(POS2 Lite 의 휴대폰 번호/이메일 고르기와 같은 모양에 아이디 한 칸) */
.login-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  margin-bottom: 20px;
  background: var(--color-border);
  border-radius: var(--radius-sm);
}
.login-tab {
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 4px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.login-tab.is-active {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.login-tab:focus {
  outline: none;
}
.login-tab:focus-visible {
  box-shadow: inset 0 0 0 2px var(--color-primary-lighter);
}
.login-label {
  display: block;
  margin-bottom: 6px;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
}
.owner-login-links {
  margin-top: 8px;
  padding-bottom: 20px;
}
.owner-join {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--color-text-secondary);
  word-break: keep-all;
}
@media (max-width: 768px) {
  #login-view {
    flex-direction: column;
  }
  .login-brand {
    flex: none;
    min-width: 0;
    max-width: none;
    padding: 20px 16px;
  }
  .login-title {
    padding: 10px 14px;
  }
  .login-title img {
    width: 120px;
  }
  .login-brand-line {
    margin-top: 12px;
    font-size: 16px;
    line-height: 24px;
  }
  .login-brand-footer {
    padding-top: 12px;
  }
  .login-main {
    align-items: flex-start;
    padding: 16px;
  }
  .contain {
    width: 100%;
    padding: 24px 20px 0 20px;
  }
}
</style>
